import { useState, useEffect } from 'react';
import { UserPlus, X, User } from 'lucide-react';
import { chUserProjectService, chProfileService } from '../services';
import { chNotificationService } from '../services/notificationService';
import { useChAuth } from '../contexts/ChAuthContext';
import { useModal } from '../../contexts/ModalContext';
import type { ChProject, ChProfile } from '../types';
import { ROLE_LABELS, isChStaffRole } from '../types';

interface Props {
  project: ChProject;
}

export function ChTeamTab({ project }: Props) {
  const { user } = useChAuth();
  const { toast } = useModal();
  const [members, setMembers] = useState<{ user_id: string; profile: ChProfile }[]>([]);
  const [allUsers, setAllUsers] = useState<ChProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  // Admin and Account manage who is on a project
  const isAdmin = isChStaffRole(user?.role);

  useEffect(() => {
    loadMembers();
  }, [project.id]);

  async function loadMembers() {
    try {
      setLoading(true);
      const data = await chUserProjectService.getProjectMembers(project.id);
      setMembers(data.map(d => ({ user_id: d.user_id, profile: d.profile })));
      if (isAdmin) {
        const users = await chProfileService.getAll();
        setAllUsers(users);
      }
    } catch {
      toast.error('Erro ao carregar a equipa');
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd(userId: string) {
    try {
      await chUserProjectService.addMember(userId, project.id);
      await loadMembers();
      setShowAdd(false);
      if (user && userId !== user.id) {
        await chNotificationService.create({
          user_id: userId,
          post_id: null,
          project_id: project.id,
          type: 'added_to_project',
          message: `${user.name} adicionou-te ao projeto "${project.name}"`,
        }).catch(() => { /* membership is saved; notification is a bonus */ });
      }
      toast.success('Membro adicionado');
    } catch {
      toast.error('Erro ao adicionar o membro');
    }
  }

  async function handleRemove(userId: string) {
    try {
      await chUserProjectService.removeMember(userId, project.id);
      await loadMembers();
    } catch {
      toast.error('Erro ao remover o membro');
    }
  }

  const nonMembers = allUsers.filter(u => !members.some(m => m.user_id === u.id));

  const roleLabels: Record<string, string> = ROLE_LABELS;

  if (loading) {
    return (
      <div className="ch-card animate-pulse space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-100" />
            <div className="h-4 w-32 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 ch-animate-in">
      <div className="ch-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm text-[var(--ch-ink)]">
            Membros ({members.length})
          </h3>
          {isAdmin && (
            <button onClick={() => setShowAdd(true)} className="ch-btn ch-btn-primary ch-btn-sm">
              <UserPlus className="w-3.5 h-3.5" />
              Adicionar
            </button>
          )}
        </div>

        {members.length === 0 ? (
          <p className="text-sm text-gray-400">Nenhum membro associado a este projeto.</p>
        ) : (
          <div className="space-y-2">
            {members.map(m => (
              <div key={m.user_id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                <div className="w-9 h-9 rounded-full bg-[var(--ch-ink)] flex items-center justify-center text-xs font-bold text-[var(--ch-lime)]">
                  {m.profile.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--ch-ink)] truncate">{m.profile.name}</p>
                  <p className="text-xs text-gray-400">{roleLabels[m.profile.role] || m.profile.role}</p>
                </div>
                {isAdmin && m.user_id !== user?.id && (
                  <button
                    onClick={() => handleRemove(m.user_id)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add member modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowAdd(false)}>
          <div className="ch-card w-full max-w-sm ch-animate-scale" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[var(--ch-ink)]">Adicionar membro</h3>
              <button onClick={() => setShowAdd(false)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            {nonMembers.length === 0 ? (
              <p className="text-sm text-gray-400">Todos os utilizadores já estão associados.</p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {nonMembers.map(u => (
                  <button
                    key={u.id}
                    onClick={() => handleAdd(u.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
                  >
                    <User className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium">{u.name}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
